import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  BookOpen,
  ShoppingBag,
  DollarSign,
  Plus,
  LogOut,
  Home,
  TrendingUp,
  Video,
  Calendar,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ users: 0, classes: 0, products: 0, orders: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    category: 'equipment',
    sport: 'Chess',
    brand: '',
    price: '',
    stock: ''
  });
  const [newMeeting, setNewMeeting] = useState({
    topic: '',
    start_time: '',
    duration: 60,
    class_id: ''
  });
  const [newClass, setNewClass] = useState({
    title: '',
    description: '',
    sport: 'Chess',
    skill_level: 'beginner',
    price: '',
    duration: '',
    schedule: '',
    max_students: 8
  });

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, meetingsRes, classesRes] = await Promise.all([
        axios.get(`${API}/dashboard/admin-stats`),
        axios.get(`${API}/zoom/meetings/all`).catch(() => ({ data: [] })),
        axios.get(`${API}/classes`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setMeetings(meetingsRes.data || []);
      setClasses(classesRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock)
      });
      toast.success('Product created successfully!');
      setShowCreateProduct(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/zoom/create-meeting`, newMeeting);
      if (response.data.success) {
        toast.success('Zoom meeting created successfully!');
        setShowCreateMeeting(false);
        fetchDashboardData();
      } else {
        toast.error(response.data.message || 'Failed to create meeting');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/classes`, {
        ...newClass,
        price: parseFloat(newClass.price),
        duration: parseInt(newClass.duration)
      });
      toast.success('Class created successfully!');
      setShowCreateClass(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create class');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" description="Manage Sun Kamal Academy platform operations and content." />

      {/* Mobile Header Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-gray-700" aria-label="Open menu" data-testid="admin-mobile-menu-toggle">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-bold text-lg">Admin</span>
        <div className="w-10" />
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && <div className="md:hidden dashboard-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed md:sticky top-0 left-0 z-40 w-64 bg-white border-r min-h-screen transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <Link to="/" className="flex items-center gap-2">
                <img src="https://customer-assets.emergentagent.com/job_sunkamalplay/artifacts/oha9raa2_logo-kamal.webp" alt="Sun Kamal Academy" className="h-10 sm:h-12" />
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1 text-gray-500" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="space-y-2">
              <button 
                onClick={() => { setActiveTab('overview'); setSidebarOpen(false); }} 
                className={`sidebar-link w-full text-left ${activeTab === 'overview' ? 'active' : ''}`} 
                data-testid="nav-dashboard"
              >
                <TrendingUp className="w-5 h-5" />
                <span>Dashboard</span>
              </button>
              <button 
                onClick={() => { setActiveTab('classes'); setSidebarOpen(false); }} 
                className={`sidebar-link w-full text-left ${activeTab === 'classes' ? 'active' : ''}`}
              >
                <BookOpen className="w-5 h-5" />
                <span>Classes</span>
              </button>
              <button 
                onClick={() => { setActiveTab('products'); setSidebarOpen(false); }} 
                className={`sidebar-link w-full text-left ${activeTab === 'products' ? 'active' : ''}`}
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Products</span>
              </button>
              <button 
                onClick={() => { setActiveTab('users'); setSidebarOpen(false); }} 
                className={`sidebar-link w-full text-left ${activeTab === 'users' ? 'active' : ''}`}
              >
                <Users className="w-5 h-5" />
                <span>Users</span>
              </button>
              <button 
                onClick={() => { setActiveTab('zoom'); setSidebarOpen(false); }} 
                className={`sidebar-link w-full text-left ${activeTab === 'zoom' ? 'active' : ''}`}
              >
                <Video className="w-5 h-5" />
                <span>Zoom Meetings</span>
              </button>
              <div className="border-t my-4 pt-4">
                <Link to="/" className="sidebar-link">
                  <Home className="w-5 h-5" />
                  <span>View Website</span>
                </Link>
                <button onClick={handleLogout} className="sidebar-link w-full text-left">
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 pt-16 md:pt-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Manage platform operations and content</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white" data-testid="stat-users">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">{stats.users}</div>
                    <div className="text-xs sm:text-sm opacity-90">Total Users</div>
                  </div>
                  <Users className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="stats-card" data-testid="stat-classes">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">{stats.classes}</div>
                    <div className="text-xs sm:text-sm opacity-90">Total Classes</div>
                  </div>
                  <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white" data-testid="stat-products">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">{stats.products}</div>
                    <div className="text-xs sm:text-sm opacity-90">Total Products</div>
                  </div>
                  <ShoppingBag className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-purple-500 to-pink-600 text-white" data-testid="stat-orders">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">{stats.orders}</div>
                    <div className="text-xs sm:text-sm opacity-90">Total Orders</div>
                  </div>
                  <DollarSign className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 hidden">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="classes" data-testid="tab-classes">Classes</TabsTrigger>
              <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
              <TabsTrigger value="zoom" data-testid="tab-zoom">Zoom Meetings</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                          <Users className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">New user registration</div>
                          <div className="text-sm text-gray-600">Just now</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white">
                          <BookOpen className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">New class created</div>
                          <div className="text-sm text-gray-600">2 hours ago</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                          <DollarSign className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">New order placed</div>
                          <div className="text-sm text-gray-600">5 hours ago</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Platform Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Active Students</span>
                        <span className="text-2xl font-bold text-blue-600">{Math.floor(stats.users * 0.6)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Active Teachers</span>
                        <span className="text-2xl font-bold text-orange-600">{Math.floor(stats.users * 0.3)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total Revenue</span>
                        <span className="text-2xl font-bold text-green-600">$0</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Pending Orders</span>
                        <span className="text-2xl font-bold text-purple-600">0</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="classes">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Classes Management</h2>
                <Dialog open={showCreateClass} onOpenChange={setShowCreateClass}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="create-class-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create New Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Class</DialogTitle>
                      <DialogDescription>Fill in the details to create a new class</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateClass} className="space-y-4" data-testid="create-class-form">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={newClass.title}
                          onChange={(e) => setNewClass({ ...newClass, title: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newClass.description}
                          onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Course/Sport Type</Label>
                          <Select value={newClass.sport} onValueChange={(value) => setNewClass({ ...newClass, sport: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Chess">Chess ♟️</SelectItem>
                              <SelectItem value="Drums">Tabla/Drums 🥁</SelectItem>
                              <SelectItem value="Tennis">Tennis 🎾</SelectItem>
                              <SelectItem value="Yoga">Yoga 🧘</SelectItem>
                              <SelectItem value="Painting">Painting 🎨</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Skill Level</Label>
                          <Select value={newClass.skill_level} onValueChange={(value) => setNewClass({ ...newClass, skill_level: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beginner">Beginner</SelectItem>
                              <SelectItem value="intermediate">Intermediate</SelectItem>
                              <SelectItem value="advanced">Advanced</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newClass.price}
                            onChange={(e) => setNewClass({ ...newClass, price: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Duration (minutes per session)</Label>
                          <Input
                            type="number"
                            value={newClass.duration}
                            onChange={(e) => setNewClass({ ...newClass, duration: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Schedule (e.g., Mon & Wed, 6:00 PM)</Label>
                        <Input
                          placeholder="Mon & Wed, 6:00-7:00 PM"
                          value={newClass.schedule}
                          onChange={(e) => setNewClass({ ...newClass, schedule: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Max Students</Label>
                        <Input
                          type="number"
                          value={newClass.max_students}
                          onChange={(e) => setNewClass({ ...newClass, max_students: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary">Create Class</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>All Classes ({classes.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {classes.length === 0 ? (
                    <div className="text-center py-8 text-gray-600">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      No classes yet. Create your first class.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {classes.map((cls, index) => (
                        <div key={cls.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                          <img src={cls.image} alt={cls.title} className="w-20 h-20 object-cover rounded" />
                          <div className="flex-1">
                            <h4 className="font-bold text-lg">{cls.title}</h4>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <BookOpen className="w-4 h-4" />
                                {cls.sport}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {cls.enrolled_count}/{cls.max_students} enrolled
                              </span>
                              <span className="font-semibold text-orange-600">${cls.price}</span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <Calendar className="w-4 h-4 inline mr-1" />
                              {cls.schedule}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">Edit</Button>
                            <Button variant="outline" size="sm" className="text-red-600">Delete</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="products">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Product Management</h2>
                <Dialog open={showCreateProduct} onOpenChange={setShowCreateProduct}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="create-product-btn">
                      <Plus className="w-4 h-4 mr-2" /> Add New Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add New Product</DialogTitle>
                      <DialogDescription>Fill in the product details</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4" data-testid="create-product-form">
                      <div>
                        <Label>Product Name</Label>
                        <Input
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newProduct.description}
                          onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Category</Label>
                          <Select value={newProduct.category} onValueChange={(value) => setNewProduct({ ...newProduct, category: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equipment">Equipment</SelectItem>
                              <SelectItem value="apparel">Apparel</SelectItem>
                              <SelectItem value="accessories">Accessories</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Select value={newProduct.sport} onValueChange={(value) => setNewProduct({ ...newProduct, sport: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Chess">Chess</SelectItem>
                              <SelectItem value="Drums">Drums / Tabla</SelectItem>
                              <SelectItem value="Yoga">Yoga</SelectItem>
                              <SelectItem value="Checkers">Checkers</SelectItem>
                              <SelectItem value="Spirituality">Spirituality</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Brand</Label>
                          <Input
                            value={newProduct.brand}
                            onChange={(e) => setNewProduct({ ...newProduct, brand: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newProduct.price}
                            onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Stock</Label>
                          <Input
                            type="number"
                            value={newProduct.stock}
                            onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <Button type="submit" className="w-full btn-primary">Add Product</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Product List</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-600">
                    {stats.products === 0 ? 'No products yet. Add your first product.' : `${stats.products} products in catalog`}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <CardTitle>User Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Total Users</div>
                        <div className="text-sm text-gray-600">All registered users</div>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{stats.users}</div>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Students</div>
                        <div className="text-sm text-gray-600">Enrolled in classes</div>
                      </div>
                      <div className="text-2xl font-bold text-green-600">{Math.floor(stats.users * 0.6)}</div>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">Teachers</div>
                        <div className="text-sm text-gray-600">Active instructors</div>
                      </div>
                      <div className="text-2xl font-bold text-orange-600">{Math.floor(stats.users * 0.3)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="zoom">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Zoom Meeting Management</h2>
                <Dialog open={showCreateMeeting} onOpenChange={setShowCreateMeeting}>
                  <DialogTrigger asChild>
                    <Button className="zoom-button" data-testid="admin-create-meeting-btn">
                      <Video className="w-4 h-4 mr-2" /> Schedule Meeting
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Schedule Zoom Meeting</DialogTitle>
                      <DialogDescription>Create a new Zoom session</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMeeting} className="space-y-4" data-testid="admin-create-meeting-form">
                      <div>
                        <Label>Meeting Topic</Label>
                        <Input
                          value={newMeeting.topic}
                          onChange={(e) => setNewMeeting({ ...newMeeting, topic: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Start Time</Label>
                        <Input
                          type="datetime-local"
                          value={newMeeting.start_time}
                          onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={newMeeting.duration}
                          onChange={(e) => setNewMeeting({ ...newMeeting, duration: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full zoom-button">Create Meeting</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>All Scheduled Meetings</CardTitle>
                </CardHeader>
                <CardContent>
                  {meetings.length === 0 ? (
                    <div className="text-center py-8 text-gray-600">
                      <Video className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      No scheduled meetings. Create one to get started.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {meetings.map((meeting, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                          <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white">
                            <Video className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold">{meeting.topic}</h4>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(meeting.start_time).toLocaleString()}</span>
                            </div>
                          </div>
                          <Button size="sm" className="zoom-button" onClick={() => window.open(meeting.join_url, '_blank')}>
                            Join
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;